import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pencil, Plus, X } from "lucide-react";
import { formatCents } from "@shared/money";
import { bookingPatch } from "@shared/schemas/bookings";
import { validateBooking } from "@shared/validation/bookings";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { changedValues, useFormValidation } from "@web/lib/formValidation";
import { recordValidationQuery } from "@web/lib/validation";
import { daysBetween, fmtDate, pctLabel, todayIn } from "@web/lib/format";
import { api, type BookingDetail, type BookingLine } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { BottomSheet, Button, Card, Inline, MoneyField, MoneyText, NavBarAction, Picker, Row, RowGroup, Stack, StatusChip, Text, Toggle, Workspace, Region, UnsavedChanges, ValidationBadge, ValidationSummary, useToast } from "@web/ui";
import { AddLineSheet, EditLineSheet } from "./LineSheets";
import { ScheduleSheet } from "./ScheduleSheet";

type Props = { b: BookingDetail; readOnly: boolean };

const lineSub = (l: BookingLine) => (l.kind === "processing_fee" ? `${pctLabel(l.pct ?? "0")} of every other line` : `${l.qty} × ${formatCents(l.kind === "discount" ? Math.abs(l.unitAmount) : l.unitAmount)}${l.kind === "discount" ? " · discount" : ""}`);

/** Pricing tab: lines · totals · schedule card with Override · damage deposit card. */
export function PricingTab({ b, readOnly }: Props) {
  const me = useMe();
  const toast = useToast();
  const today = todayIn(me.organization.timezone);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<BookingLine | null>(null);
  const [override, setOverride] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  // Removing a line changes what a client owes and cannot be undone, so the
  // cross beside Edit asks before it acts.
  const [confirmRemove, setConfirmRemove] = useState<BookingLine | null>(null);
  const command = useFormValidation(evaluation([], false));
  const [commandError, setCommandError] = useState<string | null>(null);
  const saved = useQuery(recordValidationQuery("booking", b.id));

  async function remove() {
    const l = confirmRemove;
    if (!l || readOnly || !command.begin("booking.save")) return;
    setCommandError(null);
    setRemoving(l.id);
    try {
      await api.removeLine(b.id, l.id);
      command.clear();
      setConfirmRemove(null);
    } catch (e) {
      command.reject(e);
      setCommandError(errorMessage(e));
    } finally {
      setRemoving(null);
      command.finish();
    }
  }

  async function resetSchedule() {
    if (readOnly || !command.begin("booking.save")) return;
    setCommandError(null);
    try {
      await api.resetSchedule(b.id);
      command.clear();
      toast.success("Schedule reset");
    } catch (e) {
      command.reject(e);
      setCommandError(errorMessage(e));
    } finally {
      command.finish();
    }
  }

  const balance = b.total - b.depositAmount;
  const due = (d: string | null) => (d === today ? "today" : d ? fmtDate(d) : "—");
  const days = daysBetween(today, b.eventDate);
  const when = days === 0 ? "is today" : days > 0 ? `is in ${days} day${days === 1 ? "" : "s"}` : `was ${-days} day${days === -1 ? "" : "s"} ago`;
  const explain = b.payInFull ? `Event ${when} — full payment (${formatCents(b.total)}) due ${due(b.depositDueDate)}` : `${pctLabel(b.depositPct)} deposit (${formatCents(b.depositAmount)}) due ${due(b.depositDueDate)}, balance ${formatCents(balance)} due ${due(b.balanceDueDate)}`;

  return (
    <Workspace variant="pricing">
      <UnsavedChanges dirty={false} pending={command.busy} />
      <Region area="main">
      <Stack gap={2}>
        {(command.unavailable || command.result.issues.length > 0) && <ValidationSummary result={command.result} local unavailable={command.unavailable} context="This command was not confirmed. Review the finding before retrying the same action." />}
        {commandError && <Text tone="danger">{commandError}</Text>}
        <Inline justify="between">
          <Text variant="headline">Lines</Text>
          {!readOnly && (
            <Button variant="secondary" fullWidth={false} disabled={command.busy} icon={<Plus aria-hidden size={18} />} onClick={() => command.edit(() => setAdding(true))}>
              Add
            </Button>
          )}
        </Inline>
        {(saved.isError || saved.data?.checksComplete === false) && <ValidationSummary result={saved.data} unavailable onRetry={() => void saved.refetch()} />}
        <RowGroup>
          {[...b.lines].sort((x, y) => Number(x.kind === "processing_fee") - Number(y.kind === "processing_fee") || x.sort - y.sort).map((l) => (
            <Row
              key={l.id}
              title={l.name}
              subtitle={lineSub(l)}
              trailing={
                <Stack gap={1} align="end"><Inline gap={1}>
                  <MoneyText cents={l.amount} tone={l.amount < 0 ? "danger" : "default"} />
                  {!readOnly && <NavBarAction label={`Edit ${l.name}`} disabled={command.busy} icon={<Pencil aria-hidden size={18} />} onClick={() => command.edit(() => setEditing(l))} />}
                  {!readOnly && <NavBarAction label={`Remove ${l.name}`} disabled={command.busy} icon={<X aria-hidden size={18} />} onClick={() => command.edit(() => setConfirmRemove(l))} />}
                </Inline><ValidationBadge result={saved.data ? { ...saved.data, issues: saved.data.issues.filter(issue => issue.entity.type === "booking_line" && issue.entity.id === l.id) } : undefined} unavailable={saved.isError} /></Stack>
              }
              className={removing === l.id ? "opacity-50" : ""}
            />
          ))}
          <Row title="Subtotal" trailing={<MoneyText cents={b.subtotal} />} />
          {b.discountTotal > 0 && <Row title="Discounts" trailing={<MoneyText cents={-b.discountTotal} tone="danger" />} />}
          <Row
            title={
              <Text as="span" variant="headline">
                Total
              </Text>
            }
            trailing={<MoneyText cents={b.total} variant="headline" />}
          />
        </RowGroup>
      </Stack>

      </Region>
      <Region area="aside"><Stack gap={4}>
      <Card>
        <Stack gap={3}>
          <Inline justify="between">
            <Text variant="headline">Schedule</Text>
            {Boolean(b.scheduleOverridden) && <StatusChip tone="accent">Overridden</StatusChip>}
          </Inline>
          <Text variant="callout" tone="secondary">
            {explain}
          </Text>
          {!readOnly && (
            <Inline gap={2}>
              <Button variant="secondary" disabled={command.busy} onClick={() => command.edit(() => setOverride(true))}>
                Override
              </Button>
              {Boolean(b.scheduleOverridden) && (
                <Button variant="secondary" disabled={command.busy} onClick={resetSchedule}>
                  Reset to default
                </Button>
              )}
            </Inline>
          )}
        </Stack>
      </Card>

      <DamageCard b={b} readOnly={readOnly} />
      </Stack></Region>

      {adding && <AddLineSheet b={b} open onClose={() => setAdding(false)} />}
      {editing && <EditLineSheet key={editing.id} b={b} line={editing} onClose={() => setEditing(null)} />}
      {override && <ScheduleSheet b={b} open onClose={() => setOverride(false)} />}
      <BottomSheet
        open={Boolean(confirmRemove)}
        onClose={() => setConfirmRemove(null)}
        title="Remove this line?"
        footer={<Button variant="destructive" loading={command.busy} onClick={() => void remove()}>Remove the line</Button>}
      >
        {confirmRemove && (
          <Stack gap={2}>
            <Text>{confirmRemove.name} · {formatCents(confirmRemove.amount)} comes off this booking straight away.</Text>
            <Text variant="callout" tone="secondary">The total and the balance change with it. Putting the line back means entering it again.</Text>
          </Stack>
        )}
      </BottomSheet>
    </Workspace>
  );
}

function DamageCard({ b, readOnly }: Props) {
  const toast = useToast();
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | null>(b.damageAmount);
  const [mode, setMode] = useState(b.damageMode);
  const [on, setOn] = useState(Boolean(b.damageOn));
  const body = changedValues({ damageAmount: amount, damageMode: mode, damageOn: on }, { damageAmount: b.damageAmount, damageMode: b.damageMode, damageOn: Boolean(b.damageOn) });
  const parsed = parseInput(bookingPatch, body, { type: "booking", id: b.id }, "booking.save");
  const local = validateBooking({ ...b, ...body }, { now: new Date().toISOString(), action: "booking.save", before: b });
  // This card edits only damage terms. Whole-booking findings remain in the
  // record summary; do not repeat unrelated schedule warnings in this form.
  const validation = useFormValidation(evaluation([...parsed.issues, ...local.issues.filter(issue => issue.fields.some(field => ["damageOn", "damageAmount", "damageMode"].includes(field)))], false));
  const { busy, field } = validation;
  const dirty = Object.keys(body).length > 0;

  async function save() {
    if (readOnly || !dirty || !parsed.success || !validation.begin("booking.save")) return;
    setFailureMessage(null);
    try {
      await api.patchBooking(b.id, parsed.data);
      toast.success("Damage deposit updated");
    } catch (e) {
      validation.reject(e);
      setFailureMessage(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <Card>
      <Stack gap={3}>
        <UnsavedChanges dirty={!readOnly && dirty} pending={busy} />
        {!readOnly && <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />}
        {failureMessage && <Text tone="danger">{failureMessage}</Text>}
        <Toggle label="Damage deposit" description="Never part of the total; collected or held on a card" checked={on} disabled={readOnly || busy} onChange={v => validation.edit(() => setOn(v))} />
        {on && (
          <>
            <Inline gap={2} align="start">
              <MoneyField label="Amount" className="flex-1" value={amount} {...field("damageAmount")} disabled={readOnly || busy} onChange={v => validation.edit(() => setAmount(v))} />
              <Picker label="Mode" className="flex-1" value={mode} {...field("damageMode")} disabled={readOnly || busy} onChange={v => validation.edit(() => setMode(v))} options={[{ value: "collect", label: "Collect" }, { value: "hold", label: "Card hold" }]} />
            </Inline>
          </>
        )}
        {!readOnly && dirty && <Button loading={busy} onClick={save}>Save damage deposit</Button>}
      </Stack>
    </Card>
  );
}
